import { useEffect, useState } from 'react'
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
} from '@tanstack/react-router'
import { WorkspaceDTO } from '@deepguide-ai/types'
import { useAtom } from 'jotai'
import { toast } from 'sonner'
import {
  activeWorkspaceWithStorageAtom,
  Workspace,
  usePeriodicWorkspaceRefresh,
} from '@/stores/workspaceStore'
import { activeOrganizationWithStorageAtom } from '@/stores/workspaceStore'
import { trpcClient } from '@/lib/trpc'
// TopicProvider no longer needed - using jotai atoms directly
import { SearchProvider } from '@/context/search-context'
import { ScrollArea } from '@/components/ui/scroll-area'
import PageLoading from '@/components/PageLoading'
import { DelegationContext } from '@/components/partner-delegation-banner'



export const Route = createFileRoute('/(protected)/workspaces/$workspaceId')({
  component: WorkspaceLayout,
  beforeLoad: async ({ params }) => {
    // Ensure we have a valid workspace ID
    if (!params.workspaceId) {
      throw redirect({
        to: `/workspaces`,
      })
    }
  },
})

function WorkspaceLayout() {
  const navigate = useNavigate()
  const [delegation, setDelegation] = useState<DelegationContext | null>(null);
 
  const { refetch: refetchWorkspaces } = usePeriodicWorkspaceRefresh() // Add periodic refresh for workspace data

  // All hooks must be called at the top level
  const { workspaceId: workspaceSlug } = Route.useParams()
  const [activeOrganization, setActiveOrganization] = useAtom(
    activeOrganizationWithStorageAtom
  )
  const [hasTriedRefresh, setHasTriedRefresh] = useState(false)
  const [, setActiveWorkspace] = useAtom(activeWorkspaceWithStorageAtom)


  // Find workspace from org data (already fetched)
  const matchedWorkspace = activeOrganization?.workspaces?.find(
    (ws: WorkspaceDTO) =>
      ws.slug === workspaceSlug || ws.id === workspaceSlug
  )
  const workspaceId = matchedWorkspace?.id






  // Force refresh organization data when workspace is not found
  const forceRefreshOrganization = async () => {
    try {
      console.log('Force refreshing organization data...')

      // Use the refetch function from the periodic refresh hook if available
      if (refetchWorkspaces) {
        console.log('Using TanStack Query refetch...')
        const result = await refetchWorkspaces()
        console.log(
          'Refetch result:',
          result.data?.organization?.workspaces?.length,
          'workspaces'
        )
        if (result.data?.organization) {
          return result.data.organization
        }
      }

      // Fallback to direct API call with cache busting
      console.log('Using direct API call as fallback...')
      const { organization } =
        await trpcClient.organizations.getOrganizationWithWorkspaces.query({
          // Add a timestamp to bust any potential caching
          _timestamp: Date.now(),
        } as any)

      if (organization) {
        console.log(
          'Direct API call returned',
          organization.workspaces?.length,
          'workspaces'
        )
        localStorage.setItem('auth:activeOrg', JSON.stringify(organization))
        setActiveOrganization(organization)
        console.log('Organization data force refreshed successfully')
        return organization
      }
    } catch (error) {
      console.error('Failed to force refresh organization data:', error)
      return null
    }
  }

  useEffect(() => {
    if (!workspaceId && !hasTriedRefresh && workspaceSlug) {
      console.log(
        'Workspace not found, attempting to refresh organization data...'
      )

      // Add a small delay to ensure any pending operations complete
      const refreshWithDelay = async () => {
        await new Promise((resolve) => setTimeout(resolve, 100))
        await forceRefreshOrganization()

        // Force a re-render after refresh by updating a dummy state
        setTimeout(() => {
          setHasTriedRefresh(true)
        }, 200)
      }

      refreshWithDelay()
    }
  }, [workspaceId, hasTriedRefresh, workspaceSlug])

  useEffect(() => {
    if (matchedWorkspace) {
      setActiveWorkspace(matchedWorkspace as unknown as Workspace)
    }
  }, [matchedWorkspace, setActiveWorkspace])

  useEffect(() => {
    const checkDelegation = () => {
      try {
        const stored = localStorage.getItem('partner:delegation');
        if (stored) {
          const parsed = JSON.parse(stored) as DelegationContext;

          // Check if delegation is still valid (within 24 hours)
          const age = Date.now() - parsed.timestamp;
          if (age < 24 * 60 * 60 * 1000) {
            setDelegation(parsed);
          } else {
            // Clear expired delegation
            localStorage.removeItem('partner:delegation');
          }
        }
      } catch (error) {
        console.error('Failed to check delegation context:', error);
      }
    };

    checkDelegation();

    // Re-check on storage changes
    window.addEventListener('storage', checkDelegation);
    return () => window.removeEventListener('storage', checkDelegation);
  }, []);


  // Handle workspace not found after refresh attempt
  if (!workspaceId && hasTriedRefresh) {
    const availableWorkspaces =
      activeOrganization?.workspaces?.map((w) => ({
        id: w.id,
        slug: w.slug,
        title: w.title,
      })) || []
    console.error('Workspace not found after refresh:', {
      workspaceSlug,
      availableWorkspaces,
      totalWorkspaces: availableWorkspaces.length,
    })

    // Check if there's a workspace that might match by title or partial slug
    const possibleMatches = availableWorkspaces.filter(
      (w) =>
        w.slug.includes(workspaceSlug) ||
        w.title.toLowerCase().includes(workspaceSlug.toLowerCase()) ||
        workspaceSlug.includes(w.slug)
    )

    if (possibleMatches.length > 0) {
      console.log('Possible workspace matches found:', possibleMatches)
      // Try to navigate to the first possible match
      const bestMatch = possibleMatches[0]
      console.log('Attempting to redirect to best match:', bestMatch)
      navigate({
        to: `/workspaces/${bestMatch.slug}`,
        replace: true,
      })
      return <PageLoading />
    }

    toast.error(`Workspace "${workspaceSlug}" not found`)
    // Navigate to personal workspace ("My Space") as fallback
    const personalWorkspace = activeOrganization?.workspaces?.find(
      (w: WorkspaceDTO) => w.isPersonal
    )
    if (personalWorkspace) {
      navigate({
        to: `/workspaces/${personalWorkspace.slug || personalWorkspace.id}`,
        replace: true,
      })
    } else {
      navigate({ to: `/workspaces` })
    }
    return <PageLoading />
  }

  // Show loading while we haven't tried refresh yet or while workspace is being looked up
  if (!workspaceId) {
    return <PageLoading />
  }


  return (
    <SearchProvider>
      <div className={`flex w-full ${!delegation ? 'h-screen' : 'h-[calc(100vh-56px)]'}`}>
        <ScrollArea
          className='h-full min-w-0 flex-1'
          viewClassName='[&>div]:!block [&>div]:h-full'
        >
          <Outlet />
        </ScrollArea>
      </div>
    </SearchProvider>
  )
}
